import { useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useAuthStore, { selectPortalTier } from '../stores/auth-store';
// @ts-ignore
import {
  getUserDashboard, getUserCostByProduct, getUserCostByModel,
  getUserCostByCostType, getUserTokenBreakdown, getUserDailySpendByModel,
  getEnterpriseAll,
  getDirectReportsData,
} from '../services/ai-command-service';

const PREFETCH_STALE_TIME = 5 * 60 * 1000;
const DEBOUNCE_MS = 200;

const TIER_ACCESS: Record<string, string[]> = {
  user: ['user'],
  leader: ['user', 'leader'],
  admin: ['user', 'leader', 'admin'],
  superadmin: ['user', 'leader', 'admin', 'superadmin'],
};

const ROUTE_TIER: Record<string, string> = {
  '/direct-reports': 'leader',
  '/enterprise': 'leader',
};

// Query keys MUST match the page's useQuery keys exactly, or the prefetched
// cache entry is never read.
function getConfigs(path: string, email: string, period: string) {
  if (path === '/my-dashboard') {
    return [
      { queryKey: ['my-dashboard', 'hero', email, period], queryFn: () => getUserDashboard(email, period) },
      { queryKey: ['my-dashboard', 'spend-by-product', email, period], queryFn: () => getUserCostByProduct({ email, period }) },
      { queryKey: ['my-dashboard', 'spend-by-model', email, period], queryFn: () => getUserCostByModel({ email, period }) },
      { queryKey: ['my-dashboard', 'spend-by-cost-type', email, period], queryFn: () => getUserCostByCostType({ email, period }) },
      { queryKey: ['my-dashboard', 'token-economy', email, period], queryFn: () => getUserTokenBreakdown({ email, period }) },
      { queryKey: ['my-dashboard', 'daily-spend-by-model', email, period], queryFn: () => getUserDailySpendByModel({ email, period }) },
    ];
  }
  if (path === '/direct-reports') {
    return [
      { queryKey: ['direct-reports', 'members', period, email], queryFn: () => getDirectReportsData(period) },
    ];
  }
  if (path === '/enterprise') {
    return [
      { queryKey: ['ent-all', period, undefined], queryFn: () => getEnterpriseAll({ tier: 1, period }) },
    ];
  }
  return [];
}

// period must come from MonthContext.periodParam so prefetch keys match the pages.
export function useRoutePrefetch(period: string = '30d') {
  const queryClient = useQueryClient();
  const email = useAuthStore((s) => s.userEmail) ?? '';
  const portalTier = useAuthStore(selectPortalTier);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const prefetch = useCallback((path: string) => {
    const requiredTier = ROUTE_TIER[path];
    if (requiredTier) {
      const allowed = TIER_ACCESS[portalTier] || TIER_ACCESS.user;
      if (!allowed.includes(requiredTier)) return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      getConfigs(path, email, period).forEach(({ queryKey, queryFn }) => {
        queryClient.prefetchQuery({
          queryKey,
          queryFn,
          staleTime: PREFETCH_STALE_TIME,
          retry: (count: number, error: any) => error?.status === 403 ? false : count < 1,
        });
      });
    }, DEBOUNCE_MS);
  }, [queryClient, email, period, portalTier]);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  return { prefetch, cancel };
}
